import { Link, Outlet } from 'react-router-dom';
import JFTable from '../../../component-lib/JFTable/JFTable';
import { ArchiveBoxXMarkIcon } from '@heroicons/react/24/outline';
import {
  TrendingDownOutlined,
  TrendingFlatOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../../component-lib/catalyst/dropdown';
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import { SimpleTooltip } from '../../../component-lib/Tooltip/Tooltip';

export function Trendline({ row }) {
  if (row.taskType.lastCompletionTime < row.taskType.averageCompletionTime) {
    return <TrendingDownOutlined style={{ color: '#91cf51' }} />;
  }

  if (row.taskType.lastCompletionTime > row.taskType.averageCompletionTime) {
    return <TrendingUpOutlined style={{ color: '#ec7d31' }} />;
  }

  return <TrendingFlatOutlined style={{ color: '#ffbf00' }} />;
}

export default function PlaybookTasks({
  data,
  hideHeader,
  collapsible = true,
  archiveTaskMutation,
  onCollapseToggle,
  groupParent,
}) {
  let columns = [
    {
      id: 'name',
      label: 'Name',
      width: window.GLOBAL_WIDTH || 200,
      cell: (row) => {
        const pathPrefix = row.isTaskGroup ? 'flow-groups' : 'flows';
        return (
          <div className="overflow-hidden text-ellipsis text-nowrap">
            <Link
              to={`${pathPrefix}/${row.id}`}
              className="font-bold text-blue-600 hover:underline"
            >
              {row.name}
            </Link>
          </div>
        );
      },
    },
    {
      id: 'timing',
      label: 'Timing',
      align: 'left',
      width: 125,
      cell: (row) => {
        if (row.timing === 'Offset') {
          if (row.offset === 0) {
            return groupParent ? 'Group start date' : 'Project start date';
          }
          return `Offset (${row.offset} ${row.offset === 1 ? 'day' : 'days'})`;
        }

        const timingLabel =
          row.timing === 'Sequential'
            ? 'After'
            : row.timing === 'Strictly_Sequential'
              ? 'Strictly after'
              : 'At the same time as';

        return `${timingLabel} ${row.referencedFlowName}`;
      },
    },
    {
      id: 'duration',
      label: 'Duration',
      align: 'right',
    },
    {
      id: 'weight',
      label: 'Weight',
      align: 'right',
    },
    {
      id: 'defaultAssignee',
      label: 'Default assignee',
      cell: (row) => {
        if (row.isGroup) {
          return null;
        }

        if (row.playbookOwnerAssigned) {
          return '<Project owner>';
        }

        return row.defaultAssignee;
      },
    },
    {
      id: 'taskTypeLabel',
      label: 'Type',
    },
    {
      id: 'documentsCount',
      label: 'Documents',
      align: 'right',
    },
    {
      id: 'stepsCount',
      label: 'Steps',
      align: 'right',
    },
    {
      id: 'completionTime',
      label: 'Completion times',
      align: 'center',
      disableSort: true,
      cell: (row) => {
        if (row.isGroup) {
          return null;
        }

        return (
          <div className="flex items-center justify-center gap-x-4">
            <div>{row.taskType?.averageCompletionTime}d</div>
            <Trendline row={row} />
            <div>{row.taskType?.lastCompletionTime}d</div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      label: '',
      cell: (row) => (
        <SimpleTooltip label="Actions">
          <Dropdown>
            <DropdownButton plain>
              <EllipsisHorizontalIcon />
            </DropdownButton>
            <DropdownMenu>
              <DropdownItem onClick={() => archiveTaskMutation.mutate({ cardId: row.id })}>
                <ArchiveBoxXMarkIcon />
                <DropdownLabel>Archive {row.isGroup ? 'group' : 'flow'}</DropdownLabel>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </SimpleTooltip>
      ),
    },
  ];

  if (collapsible === false) {
    columns = [{ id: 'id', label: '', cell: () => null }, ...columns];
  }

  return (
    <>
      <JFTable
        data={data.sort((a, b) => a.startDate.localeCompare(b.startDate))}
        columns={columns}
        hasCollapsedContent={(data) => (data.subtasks || []).length > 0}
        collapsedContent={
          collapsible
            ? (data) => (
                <div className="bg-gray-100">
                  <PlaybookTasks
                    data={data.subtasks || []}
                    hideHeader={true}
                    collapsible={false}
                    archiveTaskMutation={archiveTaskMutation}
                    groupParent
                  />
                </div>
              )
            : null
        }
        hideHeader={hideHeader}
        onCollapseToggle={onCollapseToggle}
      />
      <Outlet />
    </>
  );
}
